import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {useTheme} from '@material-ui/core/styles';
import SectionCard from './SectionCard';
import SettingRow from './SettingRow';
import StatusPill from './StatusPill';
import settingsStyles from '../settingsStyles';

// myweb'de resources/views/template/ altindaki grup sablonlari.
// property_web_group.template bu klasor adini tutar; bos ise 'group' kullanilir.
const GROUP_TEMPLATES = [
    {value: 'group', name: 'Group 1'},
    {value: 'group2', name: 'Group 2'},
    {value: 'group3', name: 'Group 3'},
    {value: 'group4', name: 'Group 4'},
];

/**
 * Grup sayfasi: ayni tesis grubundaki otelleri tek adreste listeleyen acilis sayfasi.
 *
 * Alanlar property_web_group tablosuyla birebir:
 *   title, domain, template, slogan, logo, address, email, phone
 *   (+ property_group_id — tesisler bu gruptan otomatik gelir, elle secilmez)
 *
 * Sayfada yalnizca web sitesi YAYINDA olan tesisler listelenir; siralama
 * property_group_mapping.order_number'a gore yapilir.
 */
export default function GroupPageCard({groupPage, propertyCount, onSave, saving, notReady}) {
    const {t} = useTranslation();
    const classes = settingsStyles();
    const theme = useTheme();
    // Telefonda form tam ekran: kucuk kutuda alanlar sikisiyor
    const fullScreenDialog = useMediaQuery(theme.breakpoints.down('xs'));

    const exists = !!(groupPage && groupPage.id);

    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        title: '',
        domain: '',
        template: 'group',
        slogan: '',
        address: '',
        email: '',
        phone: '',
    });

    // Kayitli grup sayfasi varsa formu onun degerleriyle ac
    useEffect(() => {
        if (!groupPage) return;
        setForm({
            title: groupPage.title || '',
            domain: groupPage.domain || '',
            template: groupPage.template || 'group',
            slogan: groupPage.slogan || '',
            address: groupPage.address || '',
            email: groupPage.email || '',
            phone: groupPage.phone || '',
        });
    }, [groupPage]);

    // Deger updater'a girmeden okunur: React 16/17'de SyntheticEvent havuza iade
    // edildigi icin setForm(prev => ... e.target.value) calistiginda e.target null olur.
    const set = (key) => (e) => {
        const value = e.target.value;
        setForm((prev) => ({...prev, [key]: value}));
    };

    const canSave = form.title.trim() !== '' && form.domain.trim() !== '';

    const handleSave = () => {
        if (typeof onSave !== 'function') {
            setOpen(false);
            return notReady && notReady();
        }
        onSave({...form, title: form.title.trim(), domain: form.domain.trim()});
        setOpen(false);
    };

    return (
        <SectionCard
            id="sayfalar"
            icon="fas fa-layer-group"
            title={t('enw-web-settings-pages_title', 'Sayfalar')}
            description={t('enw-web-settings-pages_desc', 'Birden fazla tesisi tek adreste toplayın')}
        >
            <SettingRow
                icon="fas fa-hotel"
                title={t('enw-web-settings-group_page_title', 'Grup Sayfası')}
                badge={exists
                    ? <StatusPill
                        tone={groupPage.is_published ? 'ok' : 'off'}
                        label={groupPage.is_published
                            ? t('enw-web-settings-group_active', 'Yayında')
                            : t('enw-web-settings-draft', 'Taslak')}
                    />
                    : <StatusPill tone="off" label={t('enw-web-settings-group_none', 'Oluşturulmadı')} />}
                active={exists}
                description={exists
                    ? t('enw-web-settings-group_page_active_desc', {
                        defaultValue: '{{domain}} · gruptaki yayında olan tesisler listeleniyor',
                        domain: groupPage.domain,
                    })
                    : t('enw-web-settings-group_page_desc', 'Aynı gruba bağlı tesisleri tek bir açılış sayfasında listeleyin. Ziyaretçi tesis seçtiğinde ilgili siteye yönlenir.')}
                control={
                    <Button
                        className={`${classes.btn} ${exists ? '' : classes.btnPrimary}`}
                        onClick={() => setOpen(true)}
                        startIcon={<i className={exists ? 'fas fa-cog' : 'fas fa-plus'} />}
                    >
                        {exists ? t('btn-edit') : t('enw-web-settings-create', 'Oluştur')}
                    </Button>
                }
            />

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={fullScreenDialog}
                classes={{paper: fullScreenDialog ? undefined : classes.dialogPaper}}
            >
                <MuiDialogTitle disableTypography className="red-bottom-border" style={{padding: '16px 24px'}}>
                    <Typography variant="h6" style={{fontWeight: 700}}>
                        {exists
                            ? t('enw-web-settings-group_dialog_edit', 'Grup Sayfasını Düzenle')
                            : t('enw-web-settings-group_dialog_title', 'Grup Sayfası Oluştur')}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpen(false)}
                        style={{position: 'absolute', right: 12, top: 12, color: '#9ca3af'}}
                    >
                        <CloseIcon className="close-button" />
                    </IconButton>
                </MuiDialogTitle>

                <DialogContent style={{padding: 24, display: 'grid', gap: 16}}>
                    <div className={classes.subGrid}>
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            label={t('enw-web-settings-group_name', 'Grup adı')}
                            placeholder="Örnek Hotels & Resorts"
                            value={form.title}
                            onChange={set('title')}
                        />
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            label={t('enw-web-settings-group_domain', 'Alan adı')}
                            placeholder="ornek-grup.com"
                            value={form.domain}
                            onChange={set('domain')}
                        />
                    </div>

                    <TextField
                        select
                        variant="outlined"
                        size="small"
                        fullWidth
                        label={t('enw-web-settings-group_template', 'Tema')}
                        value={form.template}
                        onChange={set('template')}
                    >
                        {GROUP_TEMPLATES.map((template) => (
                            <MenuItem key={template.value} value={template.value}>
                                {template.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        label={t('enw-web-settings-group_slogan', 'Slogan')}
                        placeholder={t('enw-web-settings-group_slogan_placeholder', 'Sayfanın üst bölümünde görünen kısa tanıtım metni')}
                        value={form.slogan}
                        onChange={set('slogan')}
                    />

                    <div className={classes.subGrid}>
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            label={t('enw-input-email')}
                            value={form.email}
                            onChange={set('email')}
                        />
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            label={t('enw-input-phone')}
                            value={form.phone}
                            onChange={set('phone')}
                        />
                    </div>

                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        label={t('enw-input-address')}
                        value={form.address}
                        onChange={set('address')}
                    />

                    <div className={classes.subPanel} style={{marginTop: 0}}>
                        <div className={classes.rowTitle}>
                            <i className="fas fa-info-circle" style={{color: '#8492a6'}} />
                            {t('enw-web-settings-group_properties_auto', 'Listelenecek tesisler')}
                        </div>
                        <p className={classes.hint}>
                            {typeof propertyCount === 'number'
                                ? t('enw-web-settings-group_properties_count', {
                                    defaultValue: 'Grubunuzdaki {{count}} tesis bu sayfada otomatik listelenir.',
                                    count: propertyCount,
                                })
                                : t('enw-web-settings-group_properties_desc', 'Grubunuzdaki tesisler bu sayfada otomatik listelenir.')}
                            {' '}
                            {t('enw-web-settings-group_properties_rule', 'Yalnızca web sitesi yayında olan tesisler görünür; sıralama grup tanımındaki sıraya göre yapılır.')}
                        </p>
                    </div>
                </DialogContent>

                <DialogActions style={{padding: '14px 24px', borderTop: '1px solid #f1f4f9'}}>
                    <Button className={classes.btn} onClick={() => setOpen(false)}>
                        {t('btn-cancel', 'Vazgeç')}
                    </Button>
                    <Button
                        className={`${classes.btn} ${classes.btnPrimary}`}
                        onClick={handleSave}
                        disabled={!canSave || saving}
                    >
                        {exists ? t('btn-save') : t('enw-web-settings-create', 'Oluştur')}
                    </Button>
                </DialogActions>
            </Dialog>
        </SectionCard>
    );
}
